import { Loader2, Monitor, Moon, Sun } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { languageLabel } from "@/lib/content-languages";
import type { VisitorProfile } from "@/lib/graphql/queries/generated/profile.generated";
import { SUPPORTED_LOCALES } from "@/lib/locales";
import { cn } from "@/lib/utils";
import type { WidgetConfig } from "@/lib/widget-config";
import { Section } from "./section";
import { SettingsRow } from "./settings-row";
import { Switch } from "./switch";
import type { PrefKey, PrefValue } from "./use-apply-pref";

/** Wire values for `preferences.themeMode` — must match the BE's
 *  `IsIn(['system','light','dark'])` validator. */
const THEME_OPTIONS = [
  { value: "system", icon: Monitor, labelKey: "themeAuto" },
  { value: "light", icon: Sun, labelKey: "themeLight" },
  { value: "dark", icon: Moon, labelKey: "themeDark" },
] as const;

/**
 * Preferences section — language picker, theme segmented control, and
 * desktop-notifications switch. Every control auto-saves on change via
 * `applyPref` (passed down from the parent); no explicit Save button.
 *
 * The language row is always visible — the visitor's saved language wins
 * over the operator's `localeStrategy` (cookie → strategy precedence),
 * so even when the operator pinned a default the visitor can pick their
 * own. The picker always lists the full set of widget UI locales, not
 * the operator's `supportedLocales` (which gates ARTICLE language
 * variants, not UI chrome).
 *
 * Desktop-notifications has a richer turn-on flow than the others: we
 * gate persistence on the browser permission so a "saved=true but
 * permission=denied" silent lie is impossible.
 */
export function PreferencesSection({
  profile,
  locale,
  themeMode,
  prefSaving,
  applyPref,
  onNotificationError,
  t,
}: {
  profile: VisitorProfile;
  locale: string;
  /** Live operator theme-mode preference. `"auto"` ⇒ visitor can pick;
   *  `"light"` / `"dark"` ⇒ operator forces the theme and the row is
   *  hidden entirely (visitor has no setting to change). Sourced from
   *  `config.themeMode` so a live preview change (operator flips from
   *  "Always light" to "Auto" via configOverride) reflects immediately
   *  — `profile.themeModeEditable` lags one BE fetch behind. */
  themeMode: WidgetConfig["themeMode"];
  prefSaving: PrefKey | null;
  applyPref: (key: PrefKey, value: PrefValue) => Promise<void>;
  /** Surface inline notifications-permission errors via the parent's
   *  shared banner — they're not field-level. */
  onNotificationError: (i18nKey: string | null) => void;
  t: (key: string) => string;
}) {
  // Always offer the full set of widget UI locales. Operator's
  // `supportedLocales` only filters article-language variants (handled
  // by `LanguageSelector` below articles) — it has no business
  // restricting which language the visitor can pick for the widget chrome.
  const localeOptions = SUPPORTED_LOCALES;
  // Operator's "auto" mode means the visitor controls theme. Anything
  // else (`light` / `dark`) is an explicit lock — hide the row so the
  // visitor doesn't see a control they can't change.
  const themeEditable = themeMode === "auto";
  return (
    <Section
      eyebrow={t("preferencesHeading")}
      adornment={
        prefSaving ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-normal text-wx-fg-subtle">
            <Loader2 size={10} className="animate-spin" aria-hidden="true" />
            {t("saving")}
          </span>
        ) : undefined
      }
    >
      <SettingsRow label={t("preferenceLanguage")}>
        <div className="w-40">
          <Select
            value={profile.preferences.language ?? locale}
            disabled={prefSaving === "language"}
            onValueChange={(v) => applyPref("language", v || null)}
          >
            <SelectTrigger aria-label={t("preferenceLanguage")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {localeOptions.map((code) => (
                <SelectItem key={code} value={code}>
                  {languageLabel(code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </SettingsRow>

      {themeEditable && (
        <SettingsRow label={t("preferenceThemeMode")}>
          <div className="inline-flex items-center gap-0.5 rounded-full bg-wx-bg-elevated p-0.5">
            {THEME_OPTIONS.map(({ value, icon: Icon, labelKey }) => {
              const active = (profile.preferences.themeMode ?? "system") === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  aria-label={t(labelKey)}
                  title={t(labelKey)}
                  disabled={prefSaving === "themeMode"}
                  onClick={() => applyPref("themeMode", value)}
                  className={cn(
                    "flex h-7 w-9 items-center justify-center rounded-full transition-all",
                    active
                      ? "bg-wx-bg text-wx-fg shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                      : "text-wx-fg-muted hover:text-wx-fg",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  <Icon size={13} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </SettingsRow>
      )}

      <SettingsRow
        label={t("preferenceDesktopNotifications")}
        hint={t("preferenceDesktopNotificationsHint")}
      >
        <Switch
          aria-label={t("preferenceDesktopNotifications")}
          checked={
            !!profile.preferences.desktopNotifications &&
            (typeof Notification === "undefined" || Notification.permission === "granted")
          }
          onChange={async (next) => {
            // Turn-off short-circuits — just persist false. Turn-on
            // must clear the browser gate FIRST so the saved-true
            // state always matches what we can actually deliver
            // (saved=true + permission=denied would be a silent lie).
            if (!next) {
              void applyPref("desktopNotifications", false);
              return;
            }
            if (typeof Notification === "undefined") {
              onNotificationError("notificationsRequest");
              return;
            }
            if (Notification.permission === "denied") {
              onNotificationError("notificationsBlocked");
              return;
            }
            if (Notification.permission === "default") {
              try {
                const result = await Notification.requestPermission();
                if (result !== "granted") {
                  onNotificationError("notificationsBlocked");
                  return;
                }
              } catch {
                onNotificationError("notificationsRequest");
                return;
              }
            }
            void applyPref("desktopNotifications", true);
          }}
        />
      </SettingsRow>
    </Section>
  );
}
